import MaterialGroup from './MaterialGroup.js';
import MathUtils from '../math/MathUtils.js';
import DOMUtils from '../utils/DOMUtils.js';
import Debug from '../utils/Debug.js';
import FaceOcclusion from '../occlusion/FaceOcclusion.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';

/**
 * Manages all material groups in the world
 */
export default class MaterialGroupManager {
  /**
   * Create a new material group manager
   * @param {World} world - The world instance
   */
  constructor(world) {
    this.world = world;
    this.groups = new Map(); // groupId -> MaterialGroup
    this.faceToGroup = new Map(); // "cuboidKey|face" -> groupId (for fast lookup)
    this.lastZoom = 1; // Track zoom for material refresh

    // Callback for network sync (fires on group mutations)
    this.onMutation = null;
  }

  /**
   * Check if zoom has changed and refresh materials if needed.
   * Should be called from the render loop.
   * Uses a lightweight update path that only refreshes zoom-dependent styles.
   * @param {number} currentZoom - Current camera zoom level
   */
  checkZoomAndRefresh(currentZoom) {
    if (currentZoom !== this.lastZoom) {
      this.lastZoom = currentZoom;
      this.refreshZoomStyles();
    }
  }

  /**
   * Lightweight refresh that only updates zoom-dependent CSS properties.
   * Skips DOM clearing, innerHTML manipulation, and material re-application.
   * Much faster than full refreshAllMaterials() for zoom changes.
   */
  refreshZoomStyles() {
    for (const group of this.groups.values()) {
      if (!group.material) continue;

      const materialType = group.material.type;

      // Skip materials that don't have zoom-dependent styles
      if (materialType === 'color' && !group.material.borderWidth) {
        continue;
      }

      const faces = group.getFaces();

      for (const { cuboidKey, face } of faces) {
        const cuboid = this.world.cuboids.get(cuboidKey);
        if (!cuboid) continue;

        // Collect all targets: the original + any current fragments
        const targets = [cuboid];
        const fragments = this.world.kdTree?.getFragments(cuboidKey);
        if (fragments) {
          targets.push(...fragments);
        }

        for (const target of targets) {
          if (target.isFragment && target.hiddenFaces?.includes(face)) continue;

          const faceEl = target.faceElements[face];
          if (!faceEl) continue;

          // Update border with current zoom (border width scales with zoom on Android)
          if (group.material.borderWidth) {
            const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
            const scaledBorderWidth = group.material.borderWidth * effectiveZoom;
            faceEl.style.border = `${scaledBorderWidth}px ${group.material.borderStyle || 'solid'} ${group.material.borderColor}`;
            // Re-apply hidden edges for fragments (border removal at split boundaries)
            if (target.isFragment && target.applyHiddenEdges) {
              target.applyHiddenEdges(faceEl, face);
            }
          }

          // Only update the zoom-dependent triplanar styles
          switch (materialType) {
            case 'texture':
              this.applyTextureTriplanar(faceEl, target, face, group);
              break;
            case 'video':
              this.applyVideoTriplanar(faceEl, target, face, group);
              break;
            case 'gradient':
              this.applyGradientTriplanar(faceEl, target, face, group);
              break;
            case 'pattern':
              this.applyPatternTriplanar(faceEl, target, face, group);
              break;
            case 'custom':
            case 'special':
              this.applySpecialTriplanar(faceEl, target, face, group);
              break;
            case 'text':
              this.applyTextTriplanar(faceEl, target, face, group);
              break;
            default:
              this.applyGenericTriplanar(faceEl, target, face, group);
              break;
          }
        }
      }
    }
  }

  /**
   * Refresh all material groups (re-apply with current zoom level).
   * Called when materials change or after subdivision.
   * @param {boolean} skipWorldUpdate - If true, skip world.update() calls (used after subdivision)
   */
  refreshAllMaterials(skipWorldUpdate = false) {
    for (const groupId of this.groups.keys()) {
      this.applyGroupMaterial(groupId, skipWorldUpdate);
    }
  }

  /**
   * Get current zoom level from camera.
   * More reliable than cuboid.currentZoom which may be stale.
   * @return {number} Current zoom level
   */
  getCurrentZoom() {
    return this.world.camera?.zoomLevel || 1;
  }

  /**
   * Create a new material group
   * @param {Object} options - Group options
   * @return {MaterialGroup} The created group
   */
  createGroup(options = {}) {
    const group = new MaterialGroup(options);
    this.groups.set(group.id, group);
    return group;
  }

  /**
   * Delete a material group
   * @param {string} groupId - The group ID to delete
   * @return {boolean} True if deleted
   */
  deleteGroup(groupId) {
    const group = this.groups.get(groupId);
    if (!group) return false;

    // Clean up audio source if group has a video material
    if (group.material && group.material._cleanupAudioSource) {
      group.material._cleanupAudioSource();
    }

    // Remove all face mappings for this group
    for (const serializedFace of group.faces) {
      this.faceToGroup.delete(serializedFace);
    }

    this.groups.delete(groupId);

    // Notify network sync
    if (this.onMutation) {
      this.onMutation({ op: 'deleteGroup', data: { groupId } });
    }

    return true;
  }

  /**
   * Get a group by ID
   * @param {string} groupId - The group ID
   * @return {MaterialGroup|null} The group or null
   */
  getGroup(groupId) {
    return this.groups.get(groupId) || null;
  }

  /**
   * Assign faces to a group
   * @param {string} groupId - The group ID
   * @param {Array} faces - Array of { cuboidKey, face } objects
   * @param {boolean} validate - If true, only add faces for valid cuboids (default: true)
   */
  assignFacesToGroup(groupId, faces, validate = true) {
    const group = this.groups.get(groupId);
    if (!group) return;

    faces.forEach(({ cuboidKey, face }) => {
      // Validate that the cuboid exists in the world (not a fragment key)
      if (validate && !this.world.cuboids.has(cuboidKey)) {
        Debug.warn('MaterialGroupManager', `Skipping invalid cuboid key: ${cuboidKey}`);
        return;
      }

      const serialized = MaterialGroup.serializeFace(cuboidKey, face);

      // Remove from previous group if any (ensures face is only in one group)
      const previousGroupId = this.faceToGroup.get(serialized);
      if (previousGroupId && previousGroupId !== groupId) {
        const previousGroup = this.groups.get(previousGroupId);
        if (previousGroup) {
          previousGroup.removeFace(cuboidKey, face);
        }
      }

      // Add to new group
      group.addFace(cuboidKey, face);
      this.faceToGroup.set(serialized, groupId);
    });
  }

  /**
   * Validate and clean up all groups, removing stale references
   * Call this when cuboids are deleted or to fix corrupted state
   * @returns {Object} Cleanup statistics
   */
  validateAndCleanup() {
    const stats = {
      groupsChecked: 0,
      facesRemoved: 0,
      duplicatesFixed: 0
    };

    // Track which faces we've seen to detect duplicates
    const seenFaces = new Map(); // serializedFace -> groupId

    for (const [groupId, group] of this.groups) {
      stats.groupsChecked++;
      const facesToRemove = [];

      for (const serializedFace of group.faces) {
        const { cuboidKey, face } = MaterialGroup.deserializeFace(serializedFace);

        // Check if cuboid still exists
        if (!this.world.cuboids.has(cuboidKey)) {
          facesToRemove.push({ cuboidKey, face, reason: 'stale' });
          continue;
        }

        // Check for duplicates across groups
        if (seenFaces.has(serializedFace)) {
          const existingGroupId = seenFaces.get(serializedFace);
          if (existingGroupId !== groupId) {
            // Face is in multiple groups - remove from this one
            facesToRemove.push({ cuboidKey, face, reason: 'duplicate' });
            stats.duplicatesFixed++;
            continue;
          }
        }

        seenFaces.set(serializedFace, groupId);
      }

      // Remove invalid faces
      for (const { cuboidKey, face, reason } of facesToRemove) {
        group.removeFace(cuboidKey, face);
        const serialized = MaterialGroup.serializeFace(cuboidKey, face);

        // Only remove from faceToGroup if we're the registered group
        if (this.faceToGroup.get(serialized) === groupId) {
          this.faceToGroup.delete(serialized);
        }

        stats.facesRemoved++;
        Debug.log('MaterialGroupManager', `Removed ${reason} face: ${serialized} from group ${groupId}`);
      }
    }

    // Also clean up faceToGroup map for any orphaned entries
    for (const [serializedFace, groupId] of this.faceToGroup) {
      const group = this.groups.get(groupId);
      if (!group || !group.faces.has(serializedFace)) {
        this.faceToGroup.delete(serializedFace);
        Debug.log('MaterialGroupManager', `Removed orphaned faceToGroup entry: ${serializedFace}`);
      }
    }

    return stats;
  }

  /**
   * Check if a face is already in a group (other than the specified one)
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @param {string} excludeGroupId - Group ID to exclude from check
   * @returns {MaterialGroup|null} The group containing the face, or null
   */
  getFaceGroupExcluding(cuboidKey, face, excludeGroupId) {
    const serialized = MaterialGroup.serializeFace(cuboidKey, face);
    const groupId = this.faceToGroup.get(serialized);
    if (groupId && groupId !== excludeGroupId) {
      return this.groups.get(groupId) || null;
    }
    return null;
  }

  /**
   * Remove faces from a group
   * @param {string} groupId - The group ID
   * @param {Array} faces - Array of { cuboidKey, face } objects
   */
  removeFacesFromGroup(groupId, faces) {
    const group = this.groups.get(groupId);
    if (!group) return;

    faces.forEach(({ cuboidKey, face }) => {
      const serialized = MaterialGroup.serializeFace(cuboidKey, face);
      group.removeFace(cuboidKey, face);
      this.faceToGroup.delete(serialized);

      // Clear the cached faceGroups entry on the cuboid so refreshMaterials()
      // no longer skips this face (fixes Android webpage zoom scaling)
      const cuboid = this.world.cuboids.get(cuboidKey);
      if (cuboid?.faceGroups) {
        delete cuboid.faceGroups[face];
      }
    });

    // Notify network sync
    if (this.onMutation) {
      this.onMutation({ op: 'removeGroupFaces', data: { groupId, faces } });
    }
  }

  /**
   * Get the group for a specific face
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @return {MaterialGroup|null} The group or null
   */
  getGroupForFace(cuboidKey, face) {
    const serialized = MaterialGroup.serializeFace(cuboidKey, face);
    const groupId = this.faceToGroup.get(serialized);
    return groupId ? this.groups.get(groupId) : null;
  }

  /**
   * Get all faces in the same group as a given face
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   * @return {Array} Array of { cuboidKey, face } objects
   */
  getFacesInSameGroup(cuboidKey, face) {
    const group = this.getGroupForFace(cuboidKey, face);
    return group ? group.getFaces() : [];
  }

  /**
   * Get scale value for a specific axis based on face orientation
   * @param {Object} scale - { x, y, z } scale object
   * @param {string} face - The face name
   * @return {Object} { scaleU, scaleV } for the face's UV axes
   */
  getScaleForFace(scale, face) {
    // Ensure scale is an object
    if (typeof scale === 'number') {
      scale = { x: scale, y: scale, z: scale };
    }

    switch (face) {
      case 'top':
      case 'bottom':
        // XZ plane: U = X, V = Z
        return { scaleU: scale.x, scaleV: scale.z };
      case 'front':
      case 'back':
        // XY plane: U = X, V = Y
        return { scaleU: scale.x, scaleV: scale.y };
      case 'left':
      case 'right':
        // ZY plane: U = Z, V = Y
        return { scaleU: scale.z, scaleV: scale.y };
      default:
        return { scaleU: 1, scaleV: 1 };
    }
  }

  /**
   * Calculate world-space coordinates for triplanar projection
   * @param {Cuboid} cuboid - The cuboid instance
   * @param {string} face - The face name
   * @param {Object} offset - { x, y } or { x, y, z } offset values
   * @return {Object} { u, v, worldX, worldY, worldZ } world-space coordinates
   */
  calculateWorldUV(cuboid, face, offset = { x: 0, y: 0 }) {
    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = cuboid.cubeSize * effectiveZoom;

    // Use the fragment's actual position for UV calculation
    // Each fragment's background-position must be based on its world position
    // so repeating patterns align across subdivision boundaries
    const worldX = cuboid.x * cubeSize;
    const worldY = cuboid.y * cubeSize;
    const worldZ = cuboid.z * cubeSize;

    // Cuboid dimensions (scaled by effective zoom on Android)
    const w = cuboid.width * cubeSize;
    const h = cuboid.height * cubeSize;
    const d = cuboid.depth * cubeSize;

    let u, v;
    // Offsets are in world space (no zoom scaling needed)
    const offsetX = offset.x || 0;
    const offsetY = offset.y || 0;
    const offsetZ = offset.z || 0;

    // Determine UV coordinates based on face orientation (triplanar projection)
    // For WORLD-STATIONARY textures during resize:
    // - Faces anchored to near edge (TOP, FRONT, LEFT): use direct world coord
    // - Faces anchored to far edge (BACK, RIGHT): use -(worldCoord + dimension) to compensate
    // - BOTTOM: scaleY(-1) flip doesn't affect X axis, so use same formula as TOP
    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // Anchor to near edges (worldX, worldZ+d for CSS y=0)
        u = worldX + offsetX;
        v = -(worldZ + d) + offsetZ;
        break;
      case 'bottom':
        // XZ plane projection - scaleY(-1) flips Y direction
        // After flip: element y=0 at NEAR Z, y increases as Z increases (opposite of TOP)
        // Use direct worldZ (no dimension compensation needed)
        u = worldX + offsetX;
        v = worldZ + offsetZ;
        break;
      case 'front':
        // XY plane projection - looking at front
        // Anchor to near edges
        u = worldX + offsetX;
        v = -(worldY + h) + offsetY;
        break;
      case 'back':
        // XY plane projection - looking at back (CSS x=0 at far X edge)
        // Anchor to far X edge for world-stationary when extending X
        u = -(worldX + w) + offsetX;
        v = -(worldY + h) + offsetY;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // Element positioned at far Z edge, same direction as TOP
        // Use same formula pattern as TOP: -(worldZ + d)
        u = -(worldZ + d) + offsetZ;
        v = -(worldY + h) + offsetY;
        break;
      case 'right':
        // ZY plane projection - looking at right side
        // Element positioned at NEAR Z (rightCorner1), opposite of LEFT
        // Element x increases as world Z increases
        // Use direct worldZ (no dimension compensation)
        u = worldZ + offsetZ;
        v = -(worldY + h) + offsetY;
        break;
      default:
        u = 0;
        v = 0;
    }

    return { u, v, worldX, worldY, worldZ };
  }

  /**
   * Calculate bounding box of all faces in a group
   * @param {MaterialGroup} group - The material group
   * @return {Object} { minX, maxX, minY, maxY, minZ, maxZ, width, height, depth }
   */
  calculateGroupBounds(group) {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    const faces = group.getFaces();
    for (const { cuboidKey } of faces) {
      // Only look up original cuboids - groups never contain fragment keys
      const cuboid = this.world.cuboids.get(cuboidKey);
      if (!cuboid) continue; // Skip stale keys

      // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
      const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
      const cubeSize = cuboid.cubeSize * effectiveZoom;
      const x = cuboid.x * cubeSize;
      const y = cuboid.y * cubeSize;
      const z = cuboid.z * cubeSize;
      const w = cuboid.width * cubeSize;
      const h = cuboid.height * cubeSize;
      const d = cuboid.depth * cubeSize;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + w);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + h);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z + d);
    }

    return {
      minX, maxX, minY, maxY, minZ, maxZ,
      width: maxX - minX,
      height: maxY - minY,
      depth: maxZ - minZ
    };
  }

  /**
   * Calculate triplanar UV settings for texture materials
   */
  calculateTextureSettings(cuboid, face, group) {
    const { scaleU, scaleV } = this.getScaleForFace(group.textureScale, face);
    const { u, v } = this.calculateWorldUV(cuboid, face, group.textureOffset);

    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);

    // Texture size in world units based on scale (scaled by effective zoom on Android)
    // Guard against zero/NaN: if scale or texture dimensions are missing, use safe defaults
    const safeScaleU = scaleU || 1;
    const safeScaleV = scaleV || 1;
    const texWorldWidth = (group.textureWidth || 64) / safeScaleU * effectiveZoom;
    const texWorldHeight = (group.textureHeight || 64) / safeScaleV * effectiveZoom;

    // Convert to CSS background-position (wrap within texture bounds)
    // Guard against NaN from zero-size textures (modulo with 0 or NaN = NaN)
    const bgPosX = texWorldWidth > 0 ? -((u % texWorldWidth + texWorldWidth) % texWorldWidth) : 0;
    const bgPosY = texWorldHeight > 0 ? -((v % texWorldHeight + texWorldHeight) % texWorldHeight) : 0;

    return {
      backgroundPosition: `${bgPosX}px ${bgPosY}px`,
      backgroundSize: `${texWorldWidth}px ${texWorldHeight}px`,
      backgroundRepeat: 'repeat'
    };
  }

  /**
   * Calculate triplanar settings for video materials
   */
  calculateVideoSettings(cuboid, face, group) {
    const { scaleU, scaleV } = this.getScaleForFace(group.videoScale, face);
    const { u, v } = this.calculateWorldUV(cuboid, face, group.videoOffset);

    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);

    // Video dimensions (default 320x180 for 16:9, scaled by effective zoom on Android)
    const videoWidth = 320 / scaleU * effectiveZoom;
    const videoHeight = 180 / scaleV * effectiveZoom;

    // Calculate position offset for seamless tiling
    const posX = -((u % videoWidth + videoWidth) % videoWidth);
    const posY = -((v % videoHeight + videoHeight) % videoHeight);

    return {
      objectPosition: `${posX}px ${posY}px`,
      width: videoWidth,
      height: videoHeight,
      scaleU,
      scaleV
    };
  }

  /**
   * Calculate triplanar settings for gradient materials - TRUE triplanar gradient
   * Gradient flows across all faces based on world position
   */
  calculateGradientSettings(cuboid, face, group) {
    const bounds = this.calculateGroupBounds(group);

    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = cuboid.cubeSize * effectiveZoom;

    // World position of this face
    const worldX = cuboid.x * cubeSize;
    const worldY = cuboid.y * cubeSize;
    const worldZ = cuboid.z * cubeSize;

    // Get gradient axis and calculate position along gradient
    const axis = group.gradientAxis || 'y';
    // Offset is in normalized units (0-1 = full gradient shift)
    const offset = group.gradientOffset || { x: 0, y: 0, z: 0 };
    const { scaleU, scaleV } = this.getScaleForFace(group.gradientScale, face);

    let gradientPosition; // 0-1 position along gradient
    let gradientLength;   // Total length of gradient

    // Offset is percentage of gradient (100 = shift by 100% of gradient length)
    switch (axis) {
      case 'x':
        gradientLength = bounds.width || 1;
        gradientPosition = (worldX - bounds.minX) / gradientLength + offset.x / 100;
        break;
      case 'y':
        gradientLength = bounds.height || 1;
        gradientPosition = (worldY - bounds.minY) / gradientLength + offset.y / 100;
        break;
      case 'z':
        gradientLength = bounds.depth || 1;
        gradientPosition = (worldZ - bounds.minZ) / gradientLength + offset.z / 100;
        break;
      default:
        gradientPosition = 0;
        gradientLength = 1;
    }

    // Clamp position
    gradientPosition = MathUtils.clamp(gradientPosition, 0, 1);

    return {
      position: gradientPosition,
      axis,
      angle: group.gradientAngle,
      scaleU,
      scaleV,
      bounds,
      worldX,
      worldY,
      worldZ,
      faceHeight: cuboid.height * cubeSize,
      faceWidth: face === 'left' || face === 'right' ? cuboid.depth * cubeSize : cuboid.width * cubeSize
    };
  }

  /**
   * Calculate triplanar settings for pattern materials
   */
  calculatePatternSettings(cuboid, face, group) {
    const { scaleU, scaleV } = this.getScaleForFace(group.patternScale, face);
    const { u, v } = this.calculateWorldUV(cuboid, face, group.patternOffset);

    // Pattern base size (zoom applied via CSS scale on #world)
    const patternSizeU = 20 / scaleU;
    const patternSizeV = 20 / scaleV;

    // Calculate position offset for seamless tiling
    const posX = -((u % patternSizeU + patternSizeU) % patternSizeU);
    const posY = -((v % patternSizeV + patternSizeV) % patternSizeV);

    return {
      backgroundPosition: `${posX}px ${posY}px`,
      backgroundSize: `${patternSizeU}px ${patternSizeV}px`,
      scaleU,
      scaleV
    };
  }

  /**
   * Apply group material with triplanar projection to all faces in the group
   * @param {string} groupId - The group ID
   * @param {boolean} skipWorldUpdate - If true, skip setting needsUpdate and calling world.update()
   *                                    Used when called at end of sorting to avoid overwriting triplanar settings
   */
  applyGroupMaterial(groupId, skipWorldUpdate = false) {
    const group = this.groups.get(groupId);
    if (!group || !group.material) return;

    // Tag the material so non-group code paths know not to clean up its audio source
    group.material._isGroupMaterial = true;

    const faces = group.getFaces();
    const updatedCuboids = new Set();
    const materialType = group.material.type;

    faces.forEach(({ cuboidKey, face }) => {
      // Only look up original cuboids - groups never contain fragment keys
      const cuboid = this.world.cuboids.get(cuboidKey);
      if (!cuboid) return; // Skip stale keys

      // Collect all targets: the original + any current fragments
      const targets = [cuboid];

      // Also include fragments so they inherit the material
      const fragments = this.world.kdTree?.getFragments(cuboidKey);
      if (fragments) {
        targets.push(...fragments);
      }

      // Apply to all targets (original + fragments)
      for (const target of targets) {
        // Skip hidden faces on fragments
        if (target.isFragment && target.hiddenFaces?.includes(face)) continue;

        // Get the face element
        const faceEl = target.faceElements[face];
        if (!faceEl) continue;

        // Store the material in the target
        target.materials[face] = group.material;

        // For video materials, check if video already exists to avoid recreating it
        const existingVideo = materialType === 'video' ? faceEl.querySelector('video') : null;
        const videoAlreadyExists = existingVideo && (
          existingVideo.srcObject ||  // Stream-based video (shared from parent via captureStream)
          (existingVideo.src && group.material.video && existingVideo.src.includes(group.material.video))
        );

        // Reset face element but DON'T clear innerHTML for video materials if video exists
        // Also preserve iframes for webpage materials to avoid reload on every update
        const webpageAlreadyExists = materialType === 'webpage' && faceEl.querySelector('iframe');
        const shouldKeepInnerHTML = (materialType === 'video' && videoAlreadyExists) || materialType === 'custom' || materialType === 'special' || (materialType === 'webpage' && webpageAlreadyExists);

        // Preserve selection classes before resetting
        const selectionClasses = [];
        if (faceEl.classList.contains('primary-selected')) selectionClasses.push('primary-selected');
        if (faceEl.classList.contains('multi-selected')) selectionClasses.push('multi-selected');
        if (faceEl.classList.contains('group-highlighted')) selectionClasses.push('group-highlighted');

        // Save selection highlight styles before clearing material styles.
        // Selection highlights (outline + boxShadow) are applied by Cuboid.update() and
        // would be destroyed by the material refresh. Must be restored after material application.
        const savedOutline = faceEl.style.outline;
        const savedOutlineOffset = faceEl.style.outlineOffset;
        const savedBoxShadow = faceEl.style.boxShadow;

        // Clear only material-related styles, preserving transform/positioning set by Cuboid.update()
        // DO NOT use cssText = '' as it clears critical 3D transforms
        // Note: Face lighting now uses shading-overlay instead of filter (Safari compatibility)
        faceEl.style.background = '';
        faceEl.style.backgroundColor = '';
        faceEl.style.backgroundImage = '';
        faceEl.style.backgroundSize = '';
        faceEl.style.backgroundPosition = '';
        faceEl.style.backgroundRepeat = '';
        faceEl.style.backgroundClip = '';
        faceEl.style.opacity = '';
        faceEl.style.border = '';
        faceEl.style.borderTop = '';
        faceEl.style.borderRight = '';
        faceEl.style.borderBottom = '';
        faceEl.style.borderLeft = '';
        faceEl.style.boxShadow = '';
        faceEl.style.overflow = '';

        if (!shouldKeepInnerHTML) {
          // Preserve AO overlays, character shadow overlays, and shading overlays before clearing
          const aoOverlay = faceEl.querySelector('.ao-overlay');
          const shadowOverlay = faceEl.querySelector('.character-shadow-overlay');
          const shadingOverlay = faceEl.querySelector('.shading-overlay');
          faceEl.innerHTML = '';
          if (aoOverlay) {
            faceEl.appendChild(aoOverlay);
          }
          if (shadowOverlay) {
            faceEl.appendChild(shadowOverlay);
          }
          if (shadingOverlay) {
            faceEl.appendChild(shadingOverlay);
          }
        }
        faceEl.className = `face ${face} ${selectionClasses.join(' ')}`;
        if (['right', 'left', 'front', 'back'].includes(face)) {
          faceEl.style.transformOrigin = 'left top';
        }

        // Apply base material - but skip for video if it already exists
        const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
        if (materialType === 'video' && videoAlreadyExists) {
          // Just ensure the video has correct styles, don't recreate it
          // Apply opacity from the material
          if (typeof group.material.opacity === 'number') {
            faceEl.style.opacity = group.material.opacity;
          }
          // Apply border (was cleared above)
          const scaledBorderWidth = group.material.borderWidth * effectiveZoom;
          faceEl.style.border = `${scaledBorderWidth}px ${group.material.borderStyle || 'solid'} ${group.material.borderColor}`;
          // Ensure audio source exists for unmuted video (apply was skipped above)
          if (group.material.muted === false) {
            group.material._ensureAudioSource();
            group.material._syncToAudioSource(existingVideo);
          }
        } else {
          group.material.apply(faceEl, effectiveZoom);
        }

        // Re-apply hidden edges for fragments (material.apply sets all borders, need to remove at split boundaries)
        if (target.isFragment && target.applyHiddenEdges) {
          target.applyHiddenEdges(faceEl, face);
        }

        // Apply type-specific triplanar settings
        // Use target for positioning so fragments get correct offsets
        switch (materialType) {
          case 'texture':
            this.applyTextureTriplanar(faceEl, target, face, group);
            break;
          case 'video':
            this.applyVideoTriplanar(faceEl, target, face, group);
            break;
          case 'gradient':
            this.applyGradientTriplanar(faceEl, target, face, group);
            break;
          case 'pattern':
            this.applyPatternTriplanar(faceEl, target, face, group);
            break;
          case 'color':
            faceEl.style.opacity = group.colorOpacity;
            break;
          case 'custom':
          case 'special':
            this.applySpecialTriplanar(faceEl, target, face, group);
            break;
          case 'text':
            this.applyTextTriplanar(faceEl, target, face, group);
            break;
          default:
            // For other types, apply basic positioning
            this.applyGenericTriplanar(faceEl, target, face, group);
            break;
        }

        // Also apply text triplanar if material has text content (text can be on any material type)
        if (group.material.text && materialType !== 'text') {
          this.applyTextTriplanar(faceEl, target, face, group);
        }

        // Restore selection highlight styles that were saved before material clearing.
        // The material's apply() may have set its own boxShadow (e.g., neon glow);
        // the saved value already includes both material and selection effects combined
        // by Cuboid.update(), so restoring it preserves both correctly.
        if (savedOutline) {
          faceEl.style.outline = savedOutline;
          if (savedOutlineOffset) faceEl.style.outlineOffset = savedOutlineOffset;
        }
        if (savedBoxShadow) {
          faceEl.style.boxShadow = savedBoxShadow;
        }

        // Store group reference on the target for highlighting
        if (!target.faceGroups) {
          target.faceGroups = {};
        }
        target.faceGroups[face] = groupId;

        // Mark target for update to re-apply transforms
        updatedCuboids.add(target);
      }
    });

    // Mark all affected cuboids for update so transforms are re-applied
    // Skip if called from end of sorting (to avoid overwriting triplanar settings)
    if (!skipWorldUpdate) {
      updatedCuboids.forEach(cuboid => {
        cuboid.needsUpdate = true;
      });

      // Force world update
      this.world.update();

      // Update occlusion for affected cuboids and neighbors whenever material changes
      // This handles transparency changes (opacity < 1 or glass effects)
      this._updateOcclusionForGroup(group);

      // Notify network sync (full group state) — only for user-initiated edits.
      // Render-pipeline calls (skipWorldUpdate=true) must NOT broadcast, as they would
      // spam syncGroup mutations every sort frame and corrupt remote peers' state.
      if (this.onMutation) {
        this.onMutation({ op: 'syncGroup', data: this._serializeGroup(groupId) });
      }
    }
  }

  /**
   * Serialize a group to JSON for network sync.
   * Uses the same format as World.toJSON() for groups.
   * @param {string} groupId
   * @returns {Object|null}
   * @private
   */
  _serializeGroup(groupId) {
    const group = this.groups.get(groupId);
    if (!group) return null;

    const data = {
      id: groupId,
      name: group.name || '',
      textureScale: group.textureScale,
      textureScaleLinked: group.textureScaleLinked,
      textureOffset: group.textureOffset,
      textureWidth: group.textureWidth || 256,
      textureHeight: group.textureHeight || 256,
      videoScale: group.videoScale,
      videoScaleLinked: group.videoScaleLinked,
      videoOffset: group.videoOffset,
      gradientScale: group.gradientScale,
      gradientScaleLinked: group.gradientScaleLinked,
      gradientAngle: group.gradientAngle,
      gradientAxis: group.gradientAxis,
      gradientOffset: group.gradientOffset,
      patternScale: group.patternScale,
      patternScaleLinked: group.patternScaleLinked,
      patternOffset: group.patternOffset,
      patternRotation: group.patternRotation,
      colorOpacity: group.colorOpacity,
      effectScale: group.effectScale,
      effectScaleLinked: group.effectScaleLinked,
      effectOffset: group.effectOffset,
      effectRotation: group.effectRotation,
      textScale: group.textScale,
      textScaleLinked: group.textScaleLinked,
      textOffset: group.textOffset,
      textAxis: group.textAxis,
      faces: []
    };

    if (group.material) {
      data.material = group.material.toJSON();
    }

    if (group.faces) {
      for (const serializedFace of group.faces) {
        const separatorIndex = serializedFace.lastIndexOf('|');
        const cuboidKey = serializedFace.substring(0, separatorIndex);
        const face = serializedFace.substring(separatorIndex + 1);
        data.faces.push({ cuboidKey, face });
      }
    }

    return data;
  }

  /**
   * Apply triplanar settings to a single face element.
   * Used by Cuboid.createElement() to apply group textures directly at fragment creation time,
   * avoiding the single-face texture flash that occurs when relying on refreshAllMaterials().
   * @param {HTMLElement} faceEl - The face DOM element
   * @param {Cuboid} cuboid - The cuboid (or fragment) being textured
   * @param {string} face - Face name ('top', 'front', etc.)
   * @param {string} groupId - The group ID from cuboid.faceGroups[face]
   */
  applyTriplanarToFace(faceEl, cuboid, face, groupId) {
    const group = this.groups.get(groupId);
    if (!group || !group.material) return;

    const materialType = group.material.type;
    switch (materialType) {
      case 'texture':
        this.applyTextureTriplanar(faceEl, cuboid, face, group);
        break;
      case 'video':
        this.applyVideoTriplanar(faceEl, cuboid, face, group);
        break;
      case 'gradient':
        this.applyGradientTriplanar(faceEl, cuboid, face, group);
        break;
      case 'pattern':
        this.applyPatternTriplanar(faceEl, cuboid, face, group);
        break;
      case 'color':
        if (group.colorOpacity !== undefined) {
          faceEl.style.opacity = group.colorOpacity;
        }
        break;
      case 'custom':
      case 'special':
        this.applySpecialTriplanar(faceEl, cuboid, face, group);
        break;
      case 'text':
        this.applyTextTriplanar(faceEl, cuboid, face, group);
        break;
      default:
        this.applyGenericTriplanar(faceEl, cuboid, face, group);
        break;
    }
    // Also apply text triplanar if material has text content (text can be on any material type)
    if (group.material.text && materialType !== 'text') {
      this.applyTextTriplanar(faceEl, cuboid, face, group);
    }
  }

  /**
   * Apply texture-specific triplanar settings to a face element
   */
  applyTextureTriplanar(faceEl, cuboid, face, group) {
    const settings = this.calculateTextureSettings(cuboid, face, group);
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const borderWidth = (group.material.borderWidth || 0) * effectiveZoom;
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    if (comp.x !== 0 || comp.y !== 0) {
      const m = settings.backgroundPosition.match(/^(-?[\d.]+)px\s+(-?[\d.]+)px$/);
      if (m) {
        faceEl.style.backgroundPosition = `${parseFloat(m[1]) + comp.x}px ${parseFloat(m[2]) + comp.y}px`;
      } else {
        faceEl.style.backgroundPosition = settings.backgroundPosition;
      }
    } else {
      faceEl.style.backgroundPosition = settings.backgroundPosition;
    }
    faceEl.style.backgroundSize = settings.backgroundSize;
    faceEl.style.backgroundRepeat = settings.backgroundRepeat;
  }

  /**
   * Apply video-specific triplanar settings to a face element
   * Creates seamless video tiling across all faces in the group
   */
  applyVideoTriplanar(faceEl, cuboid, face, group) {
    const bounds = this.calculateGroupBounds(group);
    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = cuboid.cubeSize * effectiveZoom;
    const scale = group.videoScale || { x: 1, y: 1, z: 1 };
    const offset = group.videoOffset || { x: 0, y: 0, z: 0 };

    // Ensure face element has opacity from material
    if (group.material && typeof group.material.opacity === 'number') {
      faceEl.style.opacity = group.material.opacity;
    }

    // Find or create video container
    let videoContainer = faceEl.querySelector('.video-triplanar-container');
    let videoEl = faceEl.querySelector('video');

    if (!videoEl) return;

    // Create container for clipping if it doesn't exist
    if (!videoContainer) {
      videoContainer = document.createElement('div');
      videoContainer.className = 'video-triplanar-container';
      videoContainer.style.position = 'absolute';
      videoContainer.style.top = '0';
      videoContainer.style.left = '0';
      videoContainer.style.width = '100%';
      videoContainer.style.height = '100%';
      videoContainer.style.overflow = 'hidden';
      videoContainer.style.pointerEvents = 'none';

      // Move video into container
      faceEl.appendChild(videoContainer);
      videoContainer.appendChild(videoEl);
    }

    // Calculate world position of this cuboid
    const worldX = cuboid.x * cubeSize;
    const worldY = cuboid.y * cubeSize;
    const worldZ = cuboid.z * cubeSize;

    // For seamless triplanar video, we keep the video at BASE size and use CSS transform
    // to scale it. This ensures adjacent faces align properly because:
    // - Video element dimensions = group dimensions (unscaled)
    // - Position = world position (unscaled)
    // - CSS transform scales the video visually
    // - Adjacent faces see adjacent portions of the video content
    // Handle each face according to its CSS transform orientation (matching applyTextureOffset):
    // - Bottom is flipped (scaleY(-1)), so X is mirrored
    // - Back is rotated 180° around Y, so X is mirrored
    // - Right faces opposite direction from left, so Z is NOT inverted for right
    let baseWidth, baseHeight, posU, posV, scaleU, scaleV;

    // Get cuboid dimensions for offset calculations
    const cuboidWidth = cuboid.width * cubeSize;
    const cuboidHeight = cuboid.height * cubeSize;
    const cuboidDepth = cuboid.depth * cubeSize;

    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // U: fragment's X offset from origin's left edge
        // V: fragment's Z offset from origin's front edge (inverted for CSS - higher Z = CSS top)
        baseWidth = bounds.width;
        baseHeight = bounds.depth;
        scaleU = scale.x;
        scaleV = scale.z;
        posU = (worldX - bounds.minX) + offset.x;
        posV = (bounds.maxZ - worldZ - cuboidDepth) + offset.z;
        break;
      case 'bottom':
        // XZ plane projection - CSS element is flipped (scaleY(-1))
        // U: offset from RIGHT edge (because X is mirrored after flip)
        // V: offset from BACK edge (Z NOT inverted due to flip)
        baseWidth = bounds.width;
        baseHeight = bounds.depth;
        scaleU = scale.x;
        scaleV = scale.z;
        posU = (bounds.maxX - worldX - cuboidWidth) + offset.x;
        posV = (worldZ - bounds.minZ) + offset.z;
        break;
      case 'front':
        // XY plane projection - looking at front
        // U: fragment's X offset from origin's left edge
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = bounds.width;
        baseHeight = bounds.height;
        scaleU = scale.x;
        scaleV = scale.y;
        posU = (worldX - bounds.minX) + offset.x;
        posV = (bounds.maxY - worldY - cuboidHeight) + offset.y;
        break;
      case 'back':
        // XY plane projection - CSS element is rotated 180° around Y
        // U: offset from RIGHT edge (because X is mirrored)
        // V: same as front (Y not affected by Y-rotation)
        baseWidth = bounds.width;
        baseHeight = bounds.height;
        scaleU = scale.x;
        scaleV = scale.y;
        posU = (bounds.maxX - worldX - cuboidWidth) + offset.x;
        posV = (bounds.maxY - worldY - cuboidHeight) + offset.y;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // U: offset from FRONT edge (inverted Z - higher Z = lower U)
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = bounds.depth;
        baseHeight = bounds.height;
        scaleU = scale.z;
        scaleV = scale.y;
        posU = (bounds.maxZ - worldZ - cuboidDepth) + offset.z;
        posV = (bounds.maxY - worldY - cuboidHeight) + offset.y;
        break;
      case 'right':
        // ZY plane projection - CSS element faces opposite direction
        // U: offset from BACK edge (Z NOT inverted for right face)
        // V: same as left
        baseWidth = bounds.depth;
        baseHeight = bounds.height;
        scaleU = scale.z;
        scaleV = scale.y;
        posU = (worldZ - bounds.minZ) + offset.z;
        posV = (bounds.maxY - worldY - cuboidHeight) + offset.y;
        break;
      default:
        return;
    }

    // Compensate for hidden border edges on fragments
    // When a border is removed, the content area expands, shifting absolute positioning
    const borderWidth = (group.material.borderWidth || 0) * effectiveZoom;
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    posU -= comp.x;
    posV -= comp.y;

    // Position the video element at base size, then scale via CSS transform
    // Key insight: position is NOT multiplied by scale - the CSS transform handles that
    // This ensures: screen_to_video(x) = (x - video_left) / scale
    // Adjacent faces will show adjacent video content
    videoEl.style.position = 'absolute';
    videoEl.style.width = `${baseWidth}px`;
    videoEl.style.height = `${baseHeight}px`;
    videoEl.style.left = `${-posU}px`;
    videoEl.style.top = `${-posV}px`;
    videoEl.style.transform = `scale(${scaleU}, ${scaleV})`;
    videoEl.style.transformOrigin = '0 0';
    videoEl.style.objectFit = 'cover';
    videoEl.style.pointerEvents = 'none';

    // Sync fragment's video time to parent for visual continuity
    if (cuboid.isFragment && cuboid.parentKey) {
      this._syncFragmentVideoTime(videoEl, cuboid.parentKey, face);
    }
  }

  /**
   * Sync a fragment's video playback time to the parent cuboid's video.
   * Each fragment has its own src-based video; this just aligns currentTime
   * so fragments show the same frame as the parent for visual continuity.
   * @param {HTMLVideoElement} fragmentVideo - The fragment's video element
   * @param {string} parentKey - The parent cuboid's key
   * @param {string} face - The face name
   */
  _syncFragmentVideoTime(fragmentVideo, parentKey, face) {
    const parentCuboid = this.world.cuboids.get(parentKey);
    if (!parentCuboid) return;

    const parentFaceEl = parentCuboid.faceElements?.[face];
    if (!parentFaceEl) return;

    const parentVideo = parentFaceEl.querySelector('video');
    if (!parentVideo || !parentVideo.currentTime) return;

    // Sync time so fragment shows same frame as parent
    const timeDiff = Math.abs(fragmentVideo.currentTime - parentVideo.currentTime);
    if (timeDiff > 0.1) {
      fragmentVideo.currentTime = parentVideo.currentTime;
    }
  }

  /**
   * Apply gradient-specific triplanar settings - TRUE triplanar gradient
   * Supports rotation, scale, and offset for both linear and radial gradients
   */
  applyGradientTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    if (!material.gradient) return;

    const { type, colors } = material.gradient;
    const bounds = this.calculateGroupBounds(group);
    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = cuboid.cubeSize * effectiveZoom;
    const scale = group.gradientScale || { x: 1, y: 1, z: 1 };
    // Offset is in normalized units (0-1 = full gradient shift)
    const offset = group.gradientOffset || { x: 0, y: 0, z: 0 };
    const angle = group.gradientAngle || 0; // Rotation in degrees
    const axis = group.gradientAxis || 'y';

    // World position of this cuboid
    const worldX = cuboid.x * cubeSize;
    const worldY = cuboid.y * cubeSize;
    const worldZ = cuboid.z * cubeSize;

    // Face dimensions
    const faceWidth = (face === 'left' || face === 'right') ? cuboid.depth * cubeSize : cuboid.width * cubeSize;
    const faceHeight = cuboid.height * cubeSize;
    const faceDepth = cuboid.depth * cubeSize;

    if (type === 'linear') {
      this.applyLinearGradientTriplanar(faceEl, {
        face, colors, bounds, scale, offset, angle, axis,
        worldX, worldY, worldZ, faceWidth, faceHeight, faceDepth, cubeSize, cuboid
      });
    } else if (type === 'radial') {
      this.applyRadialGradientTriplanar(faceEl, {
        face, colors, bounds, scale, offset,
        worldX, worldY, worldZ, faceWidth, faceHeight, faceDepth, cubeSize, cuboid
      });
    }
  }

  /**
   * Apply linear gradient with proper triplanar projection.
   * Uses percentage-based gradients with interpolated boundary colors.
   */
  applyLinearGradientTriplanar(faceEl, params) {
    const { face, colors, bounds, scale, offset, angle, axis,
            worldX, worldY, worldZ, faceWidth, faceHeight, cubeSize, cuboid } = params;

    // Calculate gradient length based on axis and scale
    let gradientLength;
    switch (axis) {
      case 'x': gradientLength = bounds.width / scale.x; break;
      case 'y': gradientLength = bounds.height / scale.y; break;
      case 'z': gradientLength = bounds.depth / scale.z; break;
      default: gradientLength = bounds.height / scale.y;
    }

    if (gradientLength <= 0) gradientLength = 1;

    const w = cuboid.width * cubeSize;
    const h = cuboid.height * cubeSize;
    const d = cuboid.depth * cubeSize;

    // Calculate face's start and end positions along gradient axis (0-1 range)
    // Offset is percentage of gradient (100 = shift by 100% of gradient length)
    let startPos, endPos;
    const offsetFractionY = offset.y / 100;
    const offsetFractionX = offset.x / 100;
    const offsetFractionZ = offset.z / 100;

    switch (axis) {
      case 'y':
        if (face === 'top' || face === 'bottom') {
          // Perpendicular to gradient - show solid color
          const yPos = face === 'top' ? worldY + h : worldY;
          const t = MathUtils.clamp((yPos - bounds.minY) / gradientLength + offsetFractionY, 0, 1);
          faceEl.style.background = this.interpolateGradientColor(colors, t);
          return;
        }
        startPos = (worldY - bounds.minY) / gradientLength + offsetFractionY;
        endPos = (worldY + h - bounds.minY) / gradientLength + offsetFractionY;
        break;

      case 'x':
        if (face === 'left' || face === 'right') {
          // Perpendicular to gradient - show solid color
          const xPos = face === 'right' ? worldX + w : worldX;
          const t = MathUtils.clamp((xPos - bounds.minX) / gradientLength + offsetFractionX, 0, 1);
          faceEl.style.background = this.interpolateGradientColor(colors, t);
          return;
        }
        startPos = (worldX - bounds.minX) / gradientLength + offsetFractionX;
        endPos = (worldX + w - bounds.minX) / gradientLength + offsetFractionX;
        break;

      case 'z':
        if (face === 'front' || face === 'back') {
          // Perpendicular to gradient - show solid color
          // Swap: front gets high Z position, back gets low Z position
          const zPos = face === 'front' ? worldZ + d : worldZ;
          const t = MathUtils.clamp((zPos - bounds.minZ) / gradientLength + offsetFractionZ, 0, 1);
          faceEl.style.background = this.interpolateGradientColor(colors, t);
          return;
        }
        startPos = (worldZ - bounds.minZ) / gradientLength + offsetFractionZ;
        endPos = (worldZ + d - bounds.minZ) / gradientLength + offsetFractionZ;
        break;

      default:
        return;
    }

    // Determine CSS gradient direction based on face and axis
    // Account for face orientation - back-facing surfaces need inverted direction
    let gradientDir;
    switch (axis) {
      case 'y':
        gradientDir = '0deg'; // bottom to top
        break;
      case 'x':
        // Front face: X goes left to right (90deg)
        // Back face: X goes right to left when viewed (270deg)
        gradientDir = (face === 'back') ? '270deg' : '90deg';
        break;
      case 'z':
        if (face === 'top' || face === 'bottom') {
          gradientDir = '180deg';
        } else {
          // Left face: Z goes left to right (90deg)
          // Right face: Z goes right to left when viewed (270deg)
          gradientDir = (face === 'right') ? '270deg' : '90deg';
        }
        break;
      default:
        gradientDir = '0deg';
    }

    // Clamp positions
    const clampedStart = MathUtils.clamp(startPos, 0, 1);
    const clampedEnd = MathUtils.clamp(endPos, 0, 1);

    // Build color stops with interpolated boundary colors
    const stops = [];

    // Start color at 0%
    stops.push(`${this.interpolateGradientColor(colors, clampedStart)} 0%`);

    // Add intermediate original colors that fall within visible range
    const minP = Math.min(startPos, endPos);
    const maxP = Math.max(startPos, endPos);
    const range = endPos - startPos;

    if (Math.abs(range) > 0.001) {
      for (let i = 0; i < colors.length; i++) {
        const colorPos = i / (colors.length - 1);
        if (colorPos > minP && colorPos < maxP) {
          const pct = ((colorPos - startPos) / range) * 100;
          if (pct > 0 && pct < 100) {
            stops.push(`${colors[i]} ${pct.toFixed(2)}%`);
          }
        }
      }
    }

    // End color at 100%
    stops.push(`${this.interpolateGradientColor(colors, clampedEnd)} 100%`);

    faceEl.style.background = `linear-gradient(${gradientDir}, ${stops.join(', ')})`;
  }

  /**
   * Get world-space corner positions for a face
   */
  getFaceWorldCorners(face, worldX, worldY, worldZ, cuboid, cubeSize) {
    const w = cuboid.width * cubeSize;
    const h = cuboid.height * cubeSize;
    const d = cuboid.depth * cubeSize;

    switch (face) {
      case 'top':
        return [
          { x: worldX, y: worldY + h, z: worldZ },
          { x: worldX + w, y: worldY + h, z: worldZ },
          { x: worldX + w, y: worldY + h, z: worldZ + d },
          { x: worldX, y: worldY + h, z: worldZ + d }
        ];
      case 'bottom':
        return [
          { x: worldX, y: worldY, z: worldZ },
          { x: worldX + w, y: worldY, z: worldZ },
          { x: worldX + w, y: worldY, z: worldZ + d },
          { x: worldX, y: worldY, z: worldZ + d }
        ];
      case 'front':
        return [
          { x: worldX, y: worldY, z: worldZ },
          { x: worldX + w, y: worldY, z: worldZ },
          { x: worldX + w, y: worldY + h, z: worldZ },
          { x: worldX, y: worldY + h, z: worldZ }
        ];
      case 'back':
        return [
          { x: worldX, y: worldY, z: worldZ + d },
          { x: worldX + w, y: worldY, z: worldZ + d },
          { x: worldX + w, y: worldY + h, z: worldZ + d },
          { x: worldX, y: worldY + h, z: worldZ + d }
        ];
      case 'left':
        return [
          { x: worldX, y: worldY, z: worldZ },
          { x: worldX, y: worldY, z: worldZ + d },
          { x: worldX, y: worldY + h, z: worldZ + d },
          { x: worldX, y: worldY + h, z: worldZ }
        ];
      case 'right':
        return [
          { x: worldX + w, y: worldY, z: worldZ },
          { x: worldX + w, y: worldY, z: worldZ + d },
          { x: worldX + w, y: worldY + h, z: worldZ + d },
          { x: worldX + w, y: worldY + h, z: worldZ }
        ];
      default:
        return [{ x: worldX, y: worldY, z: worldZ }];
    }
  }

  /**
   * Apply radial gradient with proper triplanar projection
   * Creates seamless radial gradient across all faces using 3D sphere math
   *
   * Key insight: A 3D radial gradient is a sphere of colors. When a plane (face)
   * intersects this sphere, we see concentric circles. For a 3D radius r3D and
   * perpendicular distance dz from face to center, the 2D radius is:
   * r2D = sqrt(r3D² - dz²)
   */
  applyRadialGradientTriplanar(faceEl, params) {
    const { face, colors, bounds, scale, offset,
            worldX, worldY, worldZ, cubeSize, cuboid } = params;

    // Calculate 3D group center (where the radial gradient originates)
    const centerX = (bounds.minX + bounds.maxX) / 2 + offset.x;
    const centerY = (bounds.minY + bounds.maxY) / 2 + offset.y;
    const centerZ = (bounds.minZ + bounds.maxZ) / 2 + offset.z;

    // Calculate the maximum 3D radius (half diagonal of bounding box, affected by scale)
    const avgScale = (scale.x + scale.y + scale.z) / 3;
    const maxRadius3D = Math.sqrt(
      Math.pow(bounds.width / 2, 2) +
      Math.pow(bounds.height / 2, 2) +
      Math.pow(bounds.depth / 2, 2)
    ) / avgScale;

    // Get face dimensions and calculate projection of center onto face
    let faceCenterU, faceCenterV, faceU, faceV, dz;
    let scaleU, scaleV; // Scale factors for U and V axes on this face

    switch (face) {
      case 'top':
      case 'bottom': {
        // XZ plane
        const faceY = face === 'top' ? worldY + cuboid.height * cubeSize : worldY;
        faceCenterU = centerX - worldX;
        faceCenterV = centerZ - worldZ;
        faceU = cuboid.width * cubeSize;
        faceV = cuboid.depth * cubeSize;
        dz = Math.abs(centerY - faceY) / avgScale; // Perpendicular distance to face, scaled
        scaleU = scale.x;
        scaleV = scale.z;
        break;
      }
      case 'front':
      case 'back': {
        // XY plane
        const faceZ = face === 'back' ? worldZ + cuboid.depth * cubeSize : worldZ;
        faceCenterU = centerX - worldX;
        faceCenterV = centerY - worldY;
        faceU = cuboid.width * cubeSize;
        faceV = cuboid.height * cubeSize;
        dz = Math.abs(centerZ - faceZ) / avgScale;
        scaleU = scale.x;
        scaleV = scale.y;
        break;
      }
      case 'left':
      case 'right': {
        // ZY plane
        const faceX = face === 'right' ? worldX + cuboid.width * cubeSize : worldX;
        faceCenterU = centerZ - worldZ;
        faceCenterV = centerY - worldY;
        faceU = cuboid.depth * cubeSize;
        faceV = cuboid.height * cubeSize;
        dz = Math.abs(centerX - faceX) / avgScale;
        scaleU = scale.z;
        scaleV = scale.y;
        break;
      }
      default:
        return;
    }

    // Calculate the CSS gradient center position (in pixels from top-left of face)
    // For V axis, CSS Y goes down, but our world Y goes up, so we flip
    const centerPxX = faceCenterU;
    const centerPxY = faceV - faceCenterV; // Flip for CSS coordinate system

    // Build color stops using 3D->2D radius transformation
    // For each 3D radius r3D, the 2D radius on this face is: r2D = sqrt(r3D² - dz²)
    const stops = this.buildRadialGradientStops(colors, maxRadius3D, dz, scaleU, scaleV);

    // Determine if we should use circle or ellipse based on scale differences
    const useEllipse = Math.abs(scaleU - scaleV) > 0.01;

    let gradient;
    if (useEllipse) {
      // For ellipse, we need to account for different scales in each direction
      // The ellipse radii are the 2D radius divided by the respective scale
      gradient = `radial-gradient(ellipse at ${centerPxX}px ${centerPxY}px, ${stops})`;
    } else {
      gradient = `radial-gradient(circle at ${centerPxX}px ${centerPxY}px, ${stops})`;
    }

    faceEl.style.background = gradient;
  }

  /**
   * Build CSS radial gradient color stops using 3D sphere math
   * Transforms 3D radii to 2D radii for a face at perpendicular distance dz
   */
  buildRadialGradientStops(colors, maxRadius3D, dz, scaleU, scaleV) {
    if (!colors || colors.length === 0) return 'transparent';
    if (colors.length === 1) return colors[0];

    const stops = [];
    const numColors = colors.length;

    // Check if the face is entirely inside the innermost color
    // (dz > maxRadius3D means center is beyond the face)
    if (dz >= maxRadius3D) {
      // Face is beyond the gradient - show the outermost color
      return colors[colors.length - 1];
    }

    // For each color stop, calculate where it appears on this face
    for (let i = 0; i < numColors; i++) {
      const t = i / (numColors - 1); // 0 to 1 position in gradient
      const r3D = t * maxRadius3D; // 3D radius for this color

      if (r3D <= dz) {
        // This 3D radius is "inside" the face (doesn't reach this face plane)
        // The face starts at a color interpolated between this and next
        if (i === 0) {
          // Calculate what color we start at based on dz
          const startT = dz / maxRadius3D;
          const startColor = this.interpolateGradientColor(colors, startT);
          stops.push(`${startColor} 0px`);
        }
        continue;
      }

      // Transform 3D radius to 2D radius: r2D = sqrt(r3D² - dz²)
      const r2D = Math.sqrt(r3D * r3D - dz * dz);

      // Apply average of face scales for circle, or we could use min for consistency
      const avgFaceScale = (scaleU + scaleV) / 2;
      const cssRadius = r2D / avgFaceScale;

      stops.push(`${colors[i]} ${cssRadius}px`);
    }

    // If no stops added (shouldn't happen), use first color
    if (stops.length === 0) {
      const startT = Math.min(1, dz / maxRadius3D);
      return this.interpolateGradientColor(colors, startT);
    }

    // Extend the last color to ensure full coverage
    const lastColor = colors[colors.length - 1];
    const lastRadius = Math.sqrt(maxRadius3D * maxRadius3D - dz * dz) * 2;
    stops.push(`${lastColor} ${lastRadius}px`);

    return stops.join(', ');
  }

  /**
   * Interpolate color from gradient at a given position
   * @param {Array} colors - Array of color stops
   * @param {number} position - Position 0-1
   * @return {string} Interpolated color
   */
  interpolateGradientColor(colors, position) {
    if (colors.length === 0) return '#000000';
    if (colors.length === 1) return colors[0];

    position = MathUtils.clamp(position, 0, 1);
    const index = position * (colors.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const t = index - lower;

    if (lower === upper || t === 0) return colors[lower];
    if (t === 1) return colors[upper];

    // Simple color interpolation (works for hex colors)
    return this.lerpColor(colors[lower], colors[upper], t);
  }

  /**
   * Linear interpolate between two colors
   */
  lerpColor(color1, color2, t) {
    // Parse colors (supports hex and rgb)
    const c1 = this.parseColor(color1);
    const c2 = this.parseColor(color2);

    if (!c1 || !c2) return color1;

    const r = Math.round(c1.r + (c2.r - c1.r) * t);
    const g = Math.round(c1.g + (c2.g - c1.g) * t);
    const b = Math.round(c1.b + (c2.b - c1.b) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Parse color string to RGB object
   */
  parseColor(color) {
    if (!color) return null;

    // Handle hex
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16)
        };
      } else if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16)
        };
      }
    }

    // Handle rgb/rgba
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1]),
        g: parseInt(rgbMatch[2]),
        b: parseInt(rgbMatch[3])
      };
    }

    return null;
  }

  /**
   * Adjust gradient colors to show only a portion of the full gradient
   * Uses original gradient colors mapped to face-relative positions for smooth transitions.
   * @param {Array} colors - Original gradient colors
   * @param {number} startPos - Start position 0-1 in the overall gradient
   * @param {number} endPos - End position 0-1 in the overall gradient
   * @return {Array} Color stops with percentages for CSS gradient
   */
  adjustGradientForFace(colors, startPos, endPos) {
    if (!colors || colors.length === 0) return ['#000000 0%', '#000000 100%'];
    if (colors.length === 1) return [`${colors[0]} 0%`, `${colors[0]} 100%`];

    // Clamp positions
    startPos = MathUtils.clamp(startPos, 0, 1);
    endPos = MathUtils.clamp(endPos, 0, 1);

    const range = endPos - startPos;
    if (range < 0.0001) {
      // Face spans almost no gradient range - use single color
      const color = this.interpolateGradientColor(colors, startPos);
      return [`${color} 0%`, `${color} 100%`];
    }

    const stops = [];
    const numColors = colors.length;

    // Add starting color at 0% (interpolated at startPos)
    const startColor = this.interpolateGradientColor(colors, startPos);
    stops.push(`${startColor} 0%`);

    // Add original color stops that fall within the visible range
    for (let i = 0; i < numColors; i++) {
      const colorPos = i / (numColors - 1); // 0 to 1 position of this color in original gradient

      if (colorPos > startPos && colorPos < endPos) {
        // This color stop is within our visible range
        // Map its position to a percentage within the face (0-100%)
        const facePercent = ((colorPos - startPos) / range) * 100;
        stops.push(`${colors[i]} ${facePercent.toFixed(2)}%`);
      }
    }

    // Add ending color at 100% (interpolated at endPos)
    const endColor = this.interpolateGradientColor(colors, endPos);
    stops.push(`${endColor} 100%`);

    return stops;
  }

  /**
   * Apply pattern-specific triplanar settings to a face element
   * Recreates patterns with zoom-scaled sizes for proper scaling
   */
  applyPatternTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    const pattern = material.pattern;

    if (!pattern) return;

    // Route to specific pattern handlers (only H/V stripes supported)
    switch (pattern.type) {
      case 'stripes':
        this.applyStripesTriplanar(faceEl, cuboid, face, group);
        break;
      case 'dots':
        this.applyDotsTriplanar(faceEl, cuboid, face, group);
        break;
      case 'grid':
        this.applyGridTriplanar(faceEl, cuboid, face, group);
        break;
      default:
        // Fallback for unknown pattern types
        const settings = this.calculatePatternSettings(cuboid, face, group);
        faceEl.style.backgroundPosition = settings.backgroundPosition;
        faceEl.style.backgroundSize = settings.backgroundSize;
        faceEl.style.backgroundRepeat = 'repeat';
    }
  }

  /**
   * Apply regular stripes (H/V only) with proper zoom scaling
   */
  applyStripesTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    const pattern = material.pattern;
    const colors = pattern.colors || ['#FFF', '#DDD'];
    const size = parseInt(pattern.size) || 10;
    const direction = pattern.direction || '0deg';

    const { scaleU, scaleV } = this.getScaleForFace(group.patternScale, face);

    // Calculate the period (zoom applied via CSS scale on #world)
    const period = size * 2 / ((scaleU + scaleV) / 2);
    const halfPeriod = period / 2;

    // Get UV for positioning
    const { u, v } = this.calculateWorldUV(cuboid, face, group.patternOffset);

    // H/V directions only - use non-repeating gradient with background-size
    faceEl.style.backgroundImage = `linear-gradient(
      ${direction},
      ${colors[0]} 0px,
      ${colors[0]} ${halfPeriod}px,
      ${colors[1]} ${halfPeriod}px,
      ${colors[1]} ${period}px
    )`;

    // Set size and position based on direction
    let posX, posY;
    if (direction === '0deg' || direction === '180deg') {
      // Horizontal stripes - tile vertically, only Y position matters
      faceEl.style.backgroundSize = `100% ${period}px`;
      posX = 0;
      posY = -((v % period + period) % period);
    } else {
      // Vertical stripes - tile horizontally, only X position matters
      faceEl.style.backgroundSize = `${period}px 100%`;
      posX = -((u % period + period) % period);
      posY = 0;
    }

    // Apply border (effectiveZoom for Android where zoom is baked into dimensions)
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const borderWidth = (material.borderWidth || 0) * effectiveZoom;
    const borderColor = material.borderColor || 'rgba(0, 0, 0, 0.3)';
    faceEl.style.border = `${borderWidth}px ${material.borderStyle || 'solid'} ${borderColor}`;

    // Remove borders at subdivision boundaries
    this.applyHiddenEdges(faceEl, cuboid, face);

    // Compensate background-position for hidden border edges on fragments
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    faceEl.style.backgroundPosition = `${posX + comp.x}px ${posY + comp.y}px`;
    faceEl.style.backgroundRepeat = 'repeat';
  }

  /**
   * Apply dots pattern
   */
  applyDotsTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    const pattern = material.pattern;
    const bgColor = pattern.backgroundColor || '#FFF';
    const dotColor = pattern.dotColor || '#DDD';
    const dotSize = parseInt(pattern.dotSize) || 5;
    const spacing = parseInt(pattern.spacing) || 10;

    const { scaleU, scaleV } = this.getScaleForFace(group.patternScale, face);

    // Calculate sizes (zoom applied via CSS scale on #world)
    const scaledSpacing = spacing / ((scaleU + scaleV) / 2);
    const scaledDotSize = dotSize / ((scaleU + scaleV) / 2);

    // Get UV for positioning
    const { u, v } = this.calculateWorldUV(cuboid, face, group.patternOffset);

    // Set background color
    faceEl.style.backgroundColor = bgColor;

    // Create the dot pattern
    faceEl.style.backgroundImage = `radial-gradient(
      ${dotColor} ${scaledDotSize}px,
      transparent ${scaledDotSize}px
    )`;

    faceEl.style.backgroundSize = `${scaledSpacing}px ${scaledSpacing}px`;

    // Calculate position for seamless tiling
    const posX = -((u % scaledSpacing + scaledSpacing) % scaledSpacing);
    const posY = -((v % scaledSpacing + scaledSpacing) % scaledSpacing);

    // Apply border (effectiveZoom for Android where zoom is baked into dimensions)
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const borderWidth = (material.borderWidth || 0) * effectiveZoom;
    const borderColor = material.borderColor || 'rgba(0, 0, 0, 0.3)';
    faceEl.style.border = `${borderWidth}px ${material.borderStyle || 'solid'} ${borderColor}`;

    // Remove borders at subdivision boundaries
    this.applyHiddenEdges(faceEl, cuboid, face);

    // Compensate background-position for hidden border edges on fragments
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    faceEl.style.backgroundPosition = `${posX + comp.x}px ${posY + comp.y}px`;
    faceEl.style.backgroundRepeat = 'repeat';
  }

  /**
   * Apply grid pattern
   */
  applyGridTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    const pattern = material.pattern;
    const bgColor = pattern.backgroundColor || '#FFF';
    const gridColor = pattern.gridColor || '#DDD';
    const gridSize = parseInt(pattern.gridSize) || 10;
    const lineWidth = parseInt(pattern.lineWidth) || 1;

    const { scaleU, scaleV } = this.getScaleForFace(group.patternScale, face);

    // Calculate sizes (zoom applied via CSS scale on #world)
    const scaledGridSize = gridSize / ((scaleU + scaleV) / 2);
    const scaledLineWidth = Math.max(1, lineWidth / ((scaleU + scaleV) / 2));

    // Get UV for positioning
    const { u, v } = this.calculateWorldUV(cuboid, face, group.patternOffset);

    // Set background color
    faceEl.style.backgroundColor = bgColor;

    // Create the grid pattern with zoomed sizes
    faceEl.style.backgroundImage = `
      linear-gradient(to right, ${gridColor} ${scaledLineWidth}px, transparent ${scaledLineWidth}px),
      linear-gradient(to bottom, ${gridColor} ${scaledLineWidth}px, transparent ${scaledLineWidth}px)
    `;

    faceEl.style.backgroundSize = `${scaledGridSize}px ${scaledGridSize}px`;

    // Calculate position for seamless tiling
    const posX = -((u % scaledGridSize + scaledGridSize) % scaledGridSize);
    const posY = -((v % scaledGridSize + scaledGridSize) % scaledGridSize);

    // Apply border (effectiveZoom for Android where zoom is baked into dimensions)
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const borderWidth = (material.borderWidth || 0) * effectiveZoom;
    const borderColor = material.borderColor || 'rgba(0, 0, 0, 0.3)';
    faceEl.style.border = `${borderWidth}px ${material.borderStyle || 'solid'} ${borderColor}`;

    // Remove borders at subdivision boundaries
    this.applyHiddenEdges(faceEl, cuboid, face);

    // Compensate background-position for hidden border edges on fragments
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    faceEl.style.backgroundPosition = `${posX + comp.x}px ${posY + comp.y}px`;
    faceEl.style.backgroundRepeat = 'repeat';
  }

  /**
   * Apply special effect material triplanar settings
   * Handles glass, metallic, holographic, and neon effects with proper seamless projection
   */
  applySpecialTriplanar(faceEl, cuboid, face, group) {
    const material = group.material;
    if (!material) return;

    // Clean up any previous effect before applying new one
    this.cleanupEffects(faceEl);

    const effectType = material.effectType || material.className;
    const { scaleU, scaleV } = this.getScaleForFace(group.effectScale, face);
    const { u, v, worldX, worldY, worldZ } = this.calculateWorldUV(cuboid, face, group.effectOffset);

    // Apply CSS variables for potential use by animations
    faceEl.style.setProperty('--triplanar-u', `${u}px`);
    faceEl.style.setProperty('--triplanar-v', `${v}px`);
    faceEl.style.setProperty('--triplanar-scale-u', scaleU);
    faceEl.style.setProperty('--triplanar-scale-v', scaleV);

    // Apply base color
    const baseColor = material.color || '#3498db';
    faceEl.style.backgroundColor = baseColor;

    // Apply effect-specific triplanar handling
    // Pass world position, cuboid, and offset for consistent 3D diagonal alignment
    const offset = group.effectOffset;
    switch (effectType) {
      case 'glass':
        this.applyGlassTriplanar(faceEl, material, u, v, scaleU, scaleV);
        break;
      case 'metallic':
        this.applyMetallicTriplanar(faceEl, material, u, v, scaleU, scaleV);
        break;
      case 'holographic':
        this.applyHolographicTriplanar(faceEl, material, u, v, scaleU, scaleV);
        break;
      case 'neon':
        this.applyNeonTriplanar(faceEl, material, baseColor);
        break;
      default:
        // Generic special effect - just ensure the class is applied
        if (effectType) {
          faceEl.classList.add(effectType);
        }
    }

    // Apply border (effectiveZoom for Android where zoom is baked into dimensions)
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const borderWidth = (material.borderWidth || 0) * effectiveZoom;
    const borderColor = material.borderColor || 'rgba(0, 0, 0, 0.3)';
    faceEl.style.border = `${borderWidth}px ${material.borderStyle || 'solid'} ${borderColor}`;

    // Remove borders at subdivision boundaries
    this.applyHiddenEdges(faceEl, cuboid, face);

    // Compensate background-position for hidden border edges on fragments
    // (metallic and holographic effects use backgroundPosition for tiling)
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    if (comp.x !== 0 || comp.y !== 0) {
      const bgPos = faceEl.style.backgroundPosition;
      if (bgPos) {
        const m = bgPos.match(/^(-?[\d.]+)px\s+(-?[\d.]+)px$/);
        if (m) {
          faceEl.style.backgroundPosition = `${parseFloat(m[1]) + comp.x}px ${parseFloat(m[2]) + comp.y}px`;
        }
      }
    }
  }

  /**
   * Clean up all effect containers and classes from a face element
   * Called before applying a new effect to prevent stacking
   */
  cleanupEffects(faceEl) {
    // Remove all effect containers
    const containers = faceEl.querySelectorAll('.metallic-container, .holographic-container, .stripe-container');
    containers.forEach(container => container.remove());

    // Remove effect classes
    faceEl.classList.remove('glass', 'metallic', 'holographic', 'neon');

    // Clear effect-specific styles
    faceEl.style.backdropFilter = '';
    faceEl.style.webkitBackdropFilter = '';
    faceEl.style.boxShadow = '';
    faceEl.style.color = '';
    faceEl.style.backgroundImage = '';
  }

  /**
   * Apply glass effect with triplanar support
   */
  applyGlassTriplanar(faceEl, material, u, v, scaleU, scaleV) {
    // Glass is mostly CSS-based (backdrop-filter), but we ensure proper opacity
    faceEl.classList.add('glass');
    faceEl.style.backdropFilter = 'blur(2px)';
    faceEl.style.webkitBackdropFilter = 'blur(2px)';
    // Opacity is applied by the base material.apply() method
  }

  /**
   * Apply metallic effect with seamless triplanar gradient shine (H/V directions only)
   */
  applyMetallicTriplanar(faceEl, material, u, v, scaleU, scaleV) {
    const direction = material.metallicDirection || '0deg';
    const avgScale = (scaleU + scaleV) / 2;
    // Period calculation (zoom applied via CSS scale on #world)
    const period = 20 / avgScale;

    // Remove any existing metallic container (cleanup from old technique)
    const existingContainer = faceEl.querySelector('.metallic-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    // H/V metallic gradient with background-size tiling
    faceEl.style.backgroundImage = `linear-gradient(
      ${direction},
      rgba(255, 255, 255, 0.35) 0px,
      rgba(255, 255, 255, 0.1) ${period * 0.25}px,
      rgba(0, 0, 0, 0.1) ${period * 0.5}px,
      rgba(255, 255, 255, 0.1) ${period * 0.75}px,
      rgba(255, 255, 255, 0.35) ${period}px
    )`;

    // Set size and position based on direction (like stripes)
    let posX, posY;
    if (direction === '0deg' || direction === '180deg') {
      // Horizontal metallic - tile vertically, only Y position matters
      faceEl.style.backgroundSize = `100% ${period}px`;
      posX = 0;
      posY = -((v % period + period) % period);
    } else {
      // Vertical metallic - tile horizontally, only X position matters
      faceEl.style.backgroundSize = `${period}px 100%`;
      posX = -((u % period + period) % period);
      posY = 0;
    }

    faceEl.style.backgroundPosition = `${posX}px ${posY}px`;
    faceEl.style.backgroundRepeat = 'repeat';
    faceEl.classList.add('metallic');
  }

  /**
   * Apply holographic effect with seamless triplanar rainbow gradient (H/V directions only)
   */
  applyHolographicTriplanar(faceEl, material, u, v, scaleU, scaleV) {
    const direction = material.holographicDirection || '0deg';
    const avgScale = (scaleU + scaleV) / 2;
    // Period calculation (zoom applied via CSS scale on #world)
    const period = 70 / avgScale;

    // Remove any existing holographic container (cleanup from old technique)
    const existingContainer = faceEl.querySelector('.holographic-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    // H/V holographic rainbow gradient with background-size tiling
    faceEl.style.backgroundImage = `linear-gradient(
      ${direction},
      rgb(255, 0, 0) 0px,
      rgb(255, 127, 0) ${period * 0.125}px,
      rgb(255, 255, 0) ${period * 0.25}px,
      rgb(0, 255, 0) ${period * 0.375}px,
      rgb(0, 0, 255) ${period * 0.5}px,
      rgb(0, 255, 0) ${period * 0.625}px,
      rgb(255, 255, 0) ${period * 0.75}px,
      rgb(255, 127, 0) ${period * 0.875}px,
      rgb(255, 0, 0) ${period}px
    )`;

    // Set size and position based on direction (like stripes)
    let posX, posY;
    if (direction === '0deg' || direction === '180deg') {
      // Horizontal holographic - tile vertically, only Y position matters
      faceEl.style.backgroundSize = `100% ${period}px`;
      posX = 0;
      posY = -((v % period + period) % period);
    } else {
      // Vertical holographic - tile horizontally, only X position matters
      faceEl.style.backgroundSize = `${period}px 100%`;
      posX = -((u % period + period) % period);
      posY = 0;
    }

    faceEl.style.backgroundPosition = `${posX}px ${posY}px`;
    faceEl.style.backgroundRepeat = 'repeat';
    faceEl.classList.add('holographic');
  }

  /**
   * Apply neon effect with triplanar support
   */
  applyNeonTriplanar(faceEl, material, baseColor) {
    faceEl.classList.add('neon');

    // Set the color for currentColor-based glow
    faceEl.style.color = baseColor;

    // Apply the neon glow box-shadow directly
    faceEl.style.boxShadow = `
      0 0 10px ${baseColor},
      0 0 20px ${baseColor},
      0 0 30px ${baseColor},
      inset 0 0 10px ${baseColor}
    `;

    // Animation is handled by CSS class
  }

  /**
   * Apply text-specific triplanar projection
   * Text spans seamlessly across all faces in the group, similar to video triplanar
   */
  applyTextTriplanar(faceEl, cuboid, face, group) {
    const bounds = this.calculateGroupBounds(group);
    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = cuboid.cubeSize * effectiveZoom;
    const material = group.material;

    if (!material.text) return;

    // Remove any existing face-centered text created by Material.applyText()
    // We use triplanar text instead, which spans the entire group
    const existingFaceText = faceEl.querySelector('.face-text');
    if (existingFaceText) {
      existingFaceText.remove();
    }

    const scale = group.textScale || { x: 1, y: 1, z: 1 };
    const offset = group.textOffset || { x: 0, y: 0, z: 0 };
    const axis = group.textAxis || 'xy';

    // Determine if this face should show text based on text axis setting
    const showOnFace = this.shouldShowTextOnFace(face, axis);
    if (!showOnFace) {
      const existingText = faceEl.querySelector('.text-triplanar-container');
      if (existingText) existingText.remove();
      return;
    }

    // Find or create text container (similar to video container)
    let textContainer = faceEl.querySelector('.text-triplanar-container');
    if (!textContainer) {
      textContainer = document.createElement('div');
      textContainer.className = 'text-triplanar-container';
      textContainer.style.position = 'absolute';
      textContainer.style.top = '0';
      textContainer.style.left = '0';
      textContainer.style.width = '100%';
      textContainer.style.height = '100%';
      textContainer.style.overflow = 'hidden';
      textContainer.style.pointerEvents = 'none';
      faceEl.appendChild(textContainer);
    }

    // Get or create the actual text element inside the container
    let textEl = textContainer.querySelector('.triplanar-text');
    if (!textEl) {
      textEl = document.createElement('div');
      textEl.className = 'triplanar-text';
      textContainer.appendChild(textEl);
    }

    // Calculate world position of this cuboid
    const worldX = cuboid.x * cubeSize;
    const worldY = cuboid.y * cubeSize;
    const worldZ = cuboid.z * cubeSize;

    // Offset values (no zoom scaling - CSS scale handles it)
    const scaledOffset = {
      x: offset.x,
      y: offset.y,
      z: offset.z
    };

    // For seamless triplanar text, we handle each face according to its CSS transform orientation.
    // Different faces have different mirroring due to how CSS 3D transforms work:
    // - Bottom is flipped (scaleY(-1)), so X is mirrored
    // - Back is rotated 180° around Y, so X is mirrored
    // - Right faces opposite direction from left, so Z is NOT inverted for right
    // Text element sized to group dimensions (unscaled), CSS transform scale handles visual scaling.
    let baseWidth, baseHeight, posU, posV, scaleU, scaleV;

    // Get cuboid dimensions for offset calculations
    const cuboidWidth = cuboid.width * cubeSize;
    const cuboidHeight = cuboid.height * cubeSize;
    const cuboidDepth = cuboid.depth * cubeSize;

    switch (face) {
      case 'top':
        // XZ plane projection - looking down
        // U: fragment's X offset from origin's left edge
        // V: fragment's Z offset from origin's front edge (inverted for CSS - higher Z = CSS top)
        baseWidth = bounds.width;
        baseHeight = bounds.depth;
        scaleU = scale.x;
        scaleV = scale.z;
        posU = (worldX - bounds.minX) + scaledOffset.x;
        posV = (bounds.maxZ - worldZ - cuboidDepth) + scaledOffset.z;
        break;
      case 'bottom':
        // XZ plane projection - CSS element is flipped (scaleY(-1))
        // U: offset from RIGHT edge (because X is mirrored after flip)
        // V: offset from BACK edge (Z NOT inverted due to flip)
        baseWidth = bounds.width;
        baseHeight = bounds.depth;
        scaleU = scale.x;
        scaleV = scale.z;
        posU = (bounds.maxX - worldX - cuboidWidth) + scaledOffset.x;
        posV = (worldZ - bounds.minZ) + scaledOffset.z;
        break;
      case 'front':
        // XY plane projection - looking at front
        // U: fragment's X offset from origin's left edge
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = bounds.width;
        baseHeight = bounds.height;
        scaleU = scale.x;
        scaleV = scale.y;
        posU = (worldX - bounds.minX) + scaledOffset.x;
        posV = (bounds.maxY - worldY - cuboidHeight) + scaledOffset.y;
        break;
      case 'back':
        // XY plane projection - CSS element is rotated 180° around Y
        // U: offset from RIGHT edge (because X is mirrored)
        // V: same as front (Y not affected by Y-rotation)
        baseWidth = bounds.width;
        baseHeight = bounds.height;
        scaleU = scale.x;
        scaleV = scale.y;
        posU = (bounds.maxX - worldX - cuboidWidth) + scaledOffset.x;
        posV = (bounds.maxY - worldY - cuboidHeight) + scaledOffset.y;
        break;
      case 'left':
        // ZY plane projection - looking at left side
        // U: offset from FRONT edge (inverted Z - higher Z = lower U)
        // V: fragment's Y offset from origin's top edge (inverted for CSS)
        baseWidth = bounds.depth;
        baseHeight = bounds.height;
        scaleU = scale.z;
        scaleV = scale.y;
        posU = (bounds.maxZ - worldZ - cuboidDepth) + scaledOffset.z;
        posV = (bounds.maxY - worldY - cuboidHeight) + scaledOffset.y;
        break;
      case 'right':
        // ZY plane projection - CSS element faces opposite direction
        // U: offset from BACK edge (Z NOT inverted for right face)
        // V: same as left
        baseWidth = bounds.depth;
        baseHeight = bounds.height;
        scaleU = scale.z;
        scaleV = scale.y;
        posU = (worldZ - bounds.minZ) + scaledOffset.z;
        posV = (bounds.maxY - worldY - cuboidHeight) + scaledOffset.y;
        break;
      default:
        return;
    }

    // Compensate for hidden border edges on fragments
    const borderWidth = (material.borderWidth || 0) * effectiveZoom;
    const comp = this._getBorderCompensation(cuboid, face, borderWidth);
    posU -= comp.x;
    posV -= comp.y;

    // Style the text element to span the entire group
    // Position is NOT scaled (same as video triplanar) - CSS transform handles scaling
    textEl.style.position = 'absolute';
    textEl.style.width = `${baseWidth}px`;
    textEl.style.height = `${baseHeight}px`;
    textEl.style.left = `${-posU}px`;
    textEl.style.top = `${-posV}px`;
    textEl.style.transform = `scale(${scaleU}, ${scaleV})`;
    textEl.style.transformOrigin = '0 0';

    // Parse base font size (zoom applied via CSS scale on #world)
    const baseFontSize = parseFloat(material.textSize) || 48;
    const fontUnit = (material.textSize || '48px').replace(/[\d.]/g, '') || 'px';
    const scaledFontSize = baseFontSize;

    // Text styling - use flexbox to center text within the ENTIRE GROUP area
    // This means the text appears once, centered across all faces
    textEl.style.display = 'flex';
    textEl.style.justifyContent = 'center';
    textEl.style.alignItems = 'center';
    textEl.style.color = material.textColor || '#ffffff';
    textEl.style.fontSize = `${scaledFontSize}${fontUnit}`;
    textEl.style.fontFamily = material.textFont || 'Arial, sans-serif';
    textEl.style.textAlign = 'center';
    textEl.style.whiteSpace = 'nowrap';
    textEl.style.pointerEvents = 'none';

    // Set text content
    textEl.textContent = material.text;
  }

  /**
   * Determine if text should be shown on a face based on axis setting
   */
  shouldShowTextOnFace(face, axis) {
    // axis can be: 'x', 'y', 'z', 'xy', 'xz', 'yz', 'xyz'
    switch (face) {
      case 'front':
      case 'back':
        return axis.includes('x') || axis.includes('y');
      case 'left':
      case 'right':
        return axis.includes('z') || axis.includes('y');
      case 'top':
      case 'bottom':
        return axis.includes('x') || axis.includes('z');
      default:
        return true;
    }
  }

  applyGenericTriplanar(faceEl, cuboid, face, group) {
    // Apply basic world-space positioning
    const { u, v } = this.calculateWorldUV(cuboid, face, { x: 0, y: 0 });
    faceEl.style.setProperty('--world-u', `${u}px`);
    faceEl.style.setProperty('--world-v', `${v}px`);
  }

  /**
   * Calculate background-position compensation for hidden border edges on fragments.
   * When borders are removed at split boundaries, box-sizing:border-box causes the
   * content area to expand, shifting backgrounds. This returns the pixel offsets
   * needed to compensate (matching the pattern in Cuboid.applyTextureOffset).
   * @param {Cuboid} cuboid - The cuboid (may be a fragment)
   * @param {string} face - The face name
   * @param {number} borderWidth - The material's border width (already zoom-scaled)
   * @return {{x: number, y: number}} Pixel offsets to add to backgroundPosition
   */
  _getBorderCompensation(cuboid, face, borderWidth) {
    if (!cuboid.isFragment || !cuboid.hiddenEdges || !borderWidth) {
      return { x: 0, y: 0 };
    }
    const hiddenEdges = cuboid.hiddenEdges[face] || [];
    return {
      x: hiddenEdges.includes('left') ? borderWidth : 0,
      y: hiddenEdges.includes('top') ? borderWidth : 0
    };
  }

  /**
   * Remove borders at subdivision boundaries for fragments
   * @param {HTMLElement} faceEl - The face element
   * @param {Cuboid} cuboid - The cuboid (may be a fragment with hiddenEdges)
   * @param {string} face - The face name
   */
  applyHiddenEdges(faceEl, cuboid, face) {
    // Only fragments have hiddenEdges
    if (!cuboid.isFragment || !cuboid.hiddenEdges) return;

    const edges = cuboid.hiddenEdges[face];
    if (!edges || edges.length === 0) return;

    for (const edge of edges) {
      switch (edge) {
        case 'top':
          faceEl.style.borderTop = 'none';
          break;
        case 'bottom':
          faceEl.style.borderBottom = 'none';
          break;
        case 'left':
          faceEl.style.borderLeft = 'none';
          break;
        case 'right':
          faceEl.style.borderRight = 'none';
          break;
      }
    }
  }

  // === Update methods for each setting type ===

  /**
   * Update scale for a group (any material type)
   */
  updateGroupScale(groupId, type, axis, value) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.setScale(type, axis, value);
    this.applyGroupMaterial(groupId);
  }

  /**
   * Toggle scale linked state
   */
  toggleScaleLinked(groupId, type) {
    const group = this.groups.get(groupId);
    if (!group) return;

    const linkedProp = `${type}ScaleLinked`;
    group[linkedProp] = !group[linkedProp];

    // If linking, sync all axes to X value
    if (group[linkedProp]) {
      const scaleProp = `${type}Scale`;
      const xVal = group[scaleProp].x;
      group[scaleProp] = { x: xVal, y: xVal, z: xVal };
      this.applyGroupMaterial(groupId);
    }

    return group[linkedProp];
  }

  /**
   * Update texture scale for a group
   */
  updateGroupTextureScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('texture', axis, scale);
    } else {
      group.textureScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update texture offset for a group
   */
  updateGroupTextureOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.textureOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update video scale for a group
   */
  updateGroupVideoScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('video', axis, scale);
    } else {
      group.videoScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update video offset for a group
   */
  updateGroupVideoOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.videoOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update gradient angle for a group
   */
  updateGroupGradientAngle(groupId, angle) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.gradientAngle = angle;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update gradient axis for a group
   */
  updateGroupGradientAxis(groupId, axis) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.gradientAxis = axis;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update gradient scale for a group
   */
  updateGroupGradientScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('gradient', axis, scale);
    } else {
      group.gradientScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update gradient offset for a group
   */
  updateGroupGradientOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.gradientOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update pattern scale for a group
   */
  updateGroupPatternScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('pattern', axis, scale);
    } else {
      group.patternScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update pattern offset for a group
   */
  updateGroupPatternOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.patternOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update pattern rotation for a group
   */
  updateGroupPatternRotation(groupId, rotation) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.patternRotation = rotation;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update color opacity for a group
   */
  updateGroupColorOpacity(groupId, opacity) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.colorOpacity = opacity;
    this.applyGroupMaterial(groupId);
    // Note: applyGroupMaterial now handles occlusion updates
  }

  /**
   * Update face occlusion for all cuboids in a group and their neighbors.
   * Called when material transparency changes.
   * @param {MaterialGroup} group - The material group
   * @private
   */
  _updateOcclusionForGroup(group) {
    const faces = group.getFaces();
    const updatedCuboids = new Set();

    // Collect all unique cuboids in the group
    faces.forEach(({ cuboidKey }) => {
      const cuboid = this.world.cuboids.get(cuboidKey);
      if (cuboid) {
        updatedCuboids.add(cuboid);
      }
    });

    // Update occlusion for each cuboid and its neighbors
    updatedCuboids.forEach(cuboid => {
      FaceOcclusion.updateCuboid(cuboid);
      FaceOcclusion.updateNeighbors(cuboid);
      // Also invalidate AO for nearby cuboids since transparency affects shadow casting
      // Only needed when transparency-aware AO is enabled
      if (AmbientOcclusion.considerTransparency) {
        AmbientOcclusion.invalidateForCuboid(cuboid);
      }
    });
  }

  /**
   * Update effect scale for special materials
   */
  updateGroupEffectScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('effect', axis, scale);
    } else {
      group.effectScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update effect offset for special materials
   */
  updateGroupEffectOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.effectOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update effect rotation for rotated-metallic
   */
  updateGroupEffectRotation(groupId, rotation) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.effectRotation = rotation;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update text scale for a group
   */
  updateGroupTextScale(groupId, scale, axis = 'x') {
    const group = this.groups.get(groupId);
    if (!group) return;

    if (typeof scale === 'number') {
      group.setScale('text', axis, scale);
    } else {
      group.textScale = scale;
    }
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update text offset for a group
   */
  updateGroupTextOffset(groupId, offset) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.textOffset = offset;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Update text axis projection for a group
   */
  updateGroupTextAxis(groupId, axis) {
    const group = this.groups.get(groupId);
    if (!group) return;

    group.textAxis = axis;
    this.applyGroupMaterial(groupId);
  }

  /**
   * Get all groups
   * @return {Array} Array of MaterialGroup instances
   */
  getAllGroups() {
    return Array.from(this.groups.values());
  }
}
